import { useState } from "react";
import api, { formatApiErrorDetail, setStoredToken } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };

/** Self-service password rotation — requires the current password. */
export const ChangePasswordCard = () => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.new_password.length < 8) {
            toast.error("New password must be at least 8 characters.");
            return;
        }
        if (form.new_password !== form.confirm_password) {
            toast.error("The new passwords don't match.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            if (data?.access_token) setStoredToken(data.access_token);
            setForm(EMPTY);
            toast.success("Password updated. Your session stays active on this device.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="border-[#E2E8F0]" data-testid="change-password-card">
            <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-2.5 mb-6">
                    <ShieldCheck size={20} weight="duotone" className="text-[#0066D9]" />
                    <div>
                        <h2 className="text-base md:text-lg font-semibold text-[#1F2A44]" style={{ fontFamily: "Outfit" }}>
                            Change password
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            At least 8 characters. Any outstanding reset links are cancelled.
                        </p>
                    </div>
                </div>
                <form onSubmit={submit} className="space-y-4 max-w-md">
                    {[
                        ["current_password", "Current password", "current-password"],
                        ["new_password", "New password", "new-password"],
                        ["confirm_password", "Confirm new password", "new-password"],
                    ].map(([key, label, autoComplete]) => (
                        <div key={key} className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                {label}
                            </Label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    type="password"
                                    autoComplete={autoComplete}
                                    value={form[key]}
                                    onChange={set(key)}
                                    required
                                    className="h-11 pl-9 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                    data-testid={`${key.replace("_", "-")}-input`}
                                />
                            </div>
                        </div>
                    ))}
                    <Button type="submit" disabled={saving}
                        className="bg-[#003366] hover:bg-[#0066D9] h-11"
                        data-testid="change-password-submit">
                        {saving ? "Updating…" : "Update password"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};
